import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { Heart, Bookmark, Flag, ShieldCheck, MapPin, GraduationCap, Briefcase, Users, Utensils, Eye, ArrowLeft, Calendar, Ruler, Weight } from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';
import BlockReportDialog from '../components/BlockReportDialog';
import KundaliMatch from '../components/KundaliMatch';

export default function ProfileViewPage() {
  const { id } = useParams();
  const { t } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [interestSent, setInterestSent] = useState(false);
  const [shortlisted, setShortlisted] = useState(false);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await api.get(`/profiles/${id}`);
        setProfile(res.data.profile);
      } catch (e) {
        toast.error('Profile not found');
        navigate('/search');
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [id, navigate]);

  const calcAge = (dob) => {
    if (!dob) return '';
    return Math.floor((Date.now() - new Date(dob).getTime()) / (365.25 * 24 * 60 * 60 * 1000));
  };

  const handleSendInterest = async () => {
    try {
      await api.post('/interests', { to_user_id: id });
      setInterestSent(true);
      toast.success('Interest sent!');
    } catch (e) {
      toast.error(e.response?.data?.detail || t('common.error'));
    }
  };

  const handleShortlist = async () => {
    try {
      const res = await api.post(`/shortlist/${id}`);
      setShortlisted(res.data.shortlisted);
      toast.success(res.data.shortlisted ? 'Shortlisted!' : 'Removed from shortlist');
    } catch (e) {
      toast.error(t('common.error'));
    }
  };

  if (loading) return (
    <div className="min-h-[50vh] flex items-center justify-center">
      <div className="w-8 h-8 border-3 border-[#D9381E] border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (!profile) return null;

  const age = calcAge(profile.dob);

  const InfoRow = ({ icon: Icon, label, value }) => {
    if (!value) return null;
    return (
      <div className="flex items-start gap-3 py-2">
        <Icon className="w-4 h-4 text-[#F2A900] mt-0.5 flex-shrink-0" />
        <div>
          <div className="text-xs text-[#78716C]">{label}</div>
          <div className="text-sm font-medium text-[#2D2424]">{value}</div>
        </div>
      </div>
    );
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="profile-view-page">
      <Button variant="ghost" onClick={() => navigate(-1)} className="mb-4 text-[#78716C] hover:text-[#2D2424]">
        <ArrowLeft className="w-4 h-4 mr-1" /> Back
      </Button>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Left: Photo & Actions */}
        <div className="space-y-4">
          <Card className="rounded-2xl overflow-hidden border border-[#E7E5E4]">
            <div className="aspect-[3/4] bg-gradient-to-br from-[#D9381E]/20 to-[#F2A900]/20 flex items-center justify-center">
              {profile.photos?.length > 0 ? (
                <img src={profile.photos[0]} alt={profile.name} className="w-full h-full object-cover" />
              ) : (
                <div className="text-6xl font-bold text-[#D9381E]/30">{profile.name?.[0]}</div>
              )}
            </div>
          </Card>
          <div className="flex gap-2">
            <Button
              onClick={handleSendInterest}
              disabled={interestSent || profile.id === user?.id}
              className="flex-1 bg-[#D9381E] hover:bg-[#B91C1C] text-white rounded-full py-5 font-semibold disabled:opacity-60"
              data-testid="send-interest-btn"
            >
              <Heart className={`w-4 h-4 mr-1.5 ${interestSent ? 'fill-white' : ''}`} />
              {interestSent ? t('profile.interest_sent') : t('profile.send_interest')}
            </Button>
            <Button
              variant="outline"
              onClick={handleShortlist}
              className={`rounded-full px-4 border-[#E7E5E4] ${shortlisted ? 'bg-[#F2A900]/10 text-[#F2A900] border-[#F2A900]' : ''}`}
              data-testid="shortlist-btn"
            >
              <Bookmark className={`w-4 h-4 ${shortlisted ? 'fill-[#F2A900]' : ''}`} />
            </Button>
          </div>
          {profile.id !== user?.id && (
            <BlockReportDialog userId={profile.id} userName={profile.name} onBlock={() => navigate('/search')} />
          )}
        </div>

        {/* Right: Details */}
        <div className="md:col-span-2 space-y-6">
          <Card className="p-6 rounded-2xl border border-[#E7E5E4] bg-white">
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="flex items-center gap-2">
                  <h1 className="text-2xl font-bold text-[#2D2424]">{profile.name}</h1>
                  {profile.verified_badge && <ShieldCheck className="w-5 h-5 text-green-600" />}
                </div>
                <p className="text-sm text-[#78716C] mt-1">
                  {age && `${age} ${t('common.years')}`}{profile.city && ` | ${profile.city}`}{profile.state && `, ${profile.state}`}
                </p>
              </div>
              <div className="flex items-center gap-1.5 text-xs text-[#78716C]">
                <Eye className="w-3.5 h-3.5" /> {profile.profile_views || 0} {t('profile.views')}
              </div>
            </div>
            {profile.bio && (
              <>
                <h3 className="text-sm font-semibold text-[#2D2424] mb-2">{t('profile.about')}</h3>
                <p className="text-sm text-[#78716C] leading-relaxed">{profile.bio}</p>
                <Separator className="my-4" />
              </>
            )}

            <div className="grid grid-cols-2 gap-x-6">
              <InfoRow icon={Calendar} label={t('register.dob')} value={profile.dob} />
              <InfoRow icon={Users} label={t('register.religion')} value={`${profile.religion}${profile.caste ? ` - ${profile.caste}` : ''}`} />
              <InfoRow icon={Ruler} label={t('register.height')} value={profile.height ? `${profile.height} cm` : null} />
              <InfoRow icon={Weight} label={t('register.weight')} value={profile.weight ? `${profile.weight} kg` : null} />
            </div>
          </Card>

          <Card className="p-6 rounded-2xl border border-[#E7E5E4] bg-white">
            <h3 className="text-sm font-semibold text-[#2D2424] mb-3">{t('profile.education')}</h3>
            <div className="grid grid-cols-2 gap-x-6">
              <InfoRow icon={GraduationCap} label={t('register.education_degree')} value={profile.education_degree} />
              <InfoRow icon={GraduationCap} label={t('register.education_field')} value={profile.education_field} />
              <InfoRow icon={Briefcase} label={t('register.occupation')} value={profile.occupation} />
              <InfoRow icon={Briefcase} label={t('register.company')} value={profile.company} />
              <InfoRow icon={Briefcase} label={t('register.income')} value={profile.income} />
            </div>
          </Card>

          <Card className="p-6 rounded-2xl border border-[#E7E5E4] bg-white">
            <h3 className="text-sm font-semibold text-[#2D2424] mb-3">{t('profile.family')}</h3>
            <div className="grid grid-cols-2 gap-x-6">
              <InfoRow icon={Users} label={t('register.family_type')} value={profile.family_type} />
              <InfoRow icon={Users} label={t('register.father_occupation')} value={profile.father_occupation} />
              <InfoRow icon={Users} label={t('register.mother_occupation')} value={profile.mother_occupation} />
              <InfoRow icon={Users} label={t('register.siblings')} value={profile.siblings} />
            </div>
          </Card>

          <Card className="p-6 rounded-2xl border border-[#E7E5E4] bg-white">
            <h3 className="text-sm font-semibold text-[#2D2424] mb-3">{t('profile.lifestyle')}</h3>
            <div className="grid grid-cols-2 gap-x-6">
              <InfoRow icon={Utensils} label={t('register.diet')} value={profile.diet} />
              <InfoRow icon={MapPin} label={t('register.city')} value={profile.city} />
              <InfoRow icon={MapPin} label={t('register.native_place')} value={profile.native_place} />
            </div>
          </Card>

          {(profile.pref_religion || profile.pref_caste || profile.pref_location) && (
            <Card className="p-6 rounded-2xl border border-[#E7E5E4] bg-white">
              <h3 className="text-sm font-semibold text-[#2D2424] mb-3">{t('profile.partner_pref')}</h3>
              <div className="grid grid-cols-2 gap-x-6">
                <InfoRow icon={Calendar} label={t('register.pref_age')} value={`${profile.pref_age_min || 18} - ${profile.pref_age_max || 40} yrs`} />
                <InfoRow icon={Users} label={t('register.pref_religion')} value={profile.pref_religion} />
                <InfoRow icon={Users} label={t('register.pref_caste')} value={profile.pref_caste} />
                <InfoRow icon={MapPin} label={t('register.pref_location')} value={profile.pref_location} />
                <InfoRow icon={Utensils} label={t('register.pref_diet')} value={profile.pref_diet} />
              </div>
            </Card>
          )}

          {/* Kundali Matching Section */}
          {profile.id !== user?.id && (
            <KundaliMatch profileId={profile.id} profileName={profile.name} />
          )}
        </div>
      </div>
    </div>
  );
}
